import { useEffect, useRef, useState, useCallback } from 'react';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import useDocumentTitle from '|/hooks/use-document-title';
import useEventHelper from '|/hooks/use-event-helper';
import useLoad from '|/hooks/use-load';
import useNavigateToNextStep from '|/hooks/use-navigate-to-next-step';
import {
  getScopedSlotPreference,
  saveScopedSlotPreference,
  type SlotPreferenceInput,
  type SlotPreferenceScope
} from '|/hooks/use-slot-preference';
import { useOrderSummaryContext } from './contexts/orderSummary';
import { fetchOrderSummary } from './contexts/orderSummary/orderSummary.actions';
import { useTransferScheduleContext } from './contexts/transferSchedule';
import { loadTimeSlots, setDataLoaded } from './contexts/transferSchedule/transferSchedule.actions';
import {
  getTransferTypeFriendly,
  getTransferTypeNormalized,
  normalizeActivityType
} from './utils/transferHelpers';

interface UseTransferSelectSlotVmOptions {
  slotPreferenceScope?: SlotPreferenceScope;
}

const useTransferSelectSlotVm = ({ slotPreferenceScope }: UseTransferSelectSlotVmOptions = {}) => {
  useDocumentTitle('Schedule');
  const navigateToNextStep = useNavigateToNextStep();
  const { sendLoadEvent, sendCompleteEvent } = useEventHelper();
  const load = useLoad();

  const { state: transferScheduleState, transferScheduleDispatch } = useTransferScheduleContext();
  const { state: orderSummaryState, dispatch: orderSummaryDispatch } = useOrderSummaryContext();

  const [isInitialized, setIsInitialized] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasFetched = useRef(false);

  const getSavedSlotPreference = useCallback(
    () => getScopedSlotPreference(slotPreferenceScope),
    [slotPreferenceScope]
  );

  const saveSlotPreference = useCallback(
    (input: SlotPreferenceInput) => {
      saveScopedSlotPreference(slotPreferenceScope, input);
    },
    [slotPreferenceScope]
  );

  const initialize = useCallback(async () => {
    try {
      const [, timeSlotsResult] = await Promise.all([
        fetchOrderSummary(orderSummaryDispatch),
        loadTimeSlots(transferScheduleDispatch)
      ]);

      setDataLoaded(transferScheduleDispatch, true);

      // Pre-select slot from saved preference if returning to this step.
      // Match against the displayed pool (windows vs fixed) on routeId + utcStartTime and the
      // saved activity type, so a slot saved under a different activity isn't restored.
      const savedPref = getSavedSlotPreference();
      if (savedPref?.utcStartTime && timeSlotsResult) {
        const pool: {
          date: string;
          times: {
            activityType: string;
            optionId: string;
            routeId: string;
            time: string;
            utcStartTime: number;
            endRange?: string;
          }[];
        }[] = timeSlotsResult.isFlexibleDelivery
          ? timeSlotsResult.timeWindows || []
          : timeSlotsResult.timeSlots || [];

        const savedActivityType = normalizeActivityType(savedPref.activityType);
        const matchesSaved = (t: { routeId: string; utcStartTime: number; activityType: string }) =>
          t.routeId === savedPref.routeId &&
          t.utcStartTime === savedPref.utcStartTime &&
          normalizeActivityType(t.activityType) === savedActivityType;

        const matchingDate = pool.find(dateGroup => dateGroup.times.some(matchesSaved));
        if (matchingDate) {
          transferScheduleDispatch({ type: 'SELECT_DATE', payload: { date: matchingDate.date } });
          const matchingTime = matchingDate.times.find(matchesSaved);
          if (matchingTime) {
            transferScheduleDispatch({
              type: 'SELECT_TIME',
              payload: {
                activityType: matchingTime.activityType,
                routeId: matchingTime.routeId,
                optionId: matchingTime.optionId,
                utcStartTime: matchingTime.utcStartTime,
                time: matchingTime.time,
                endRange: matchingTime.endRange ?? ''
              }
            });

            // Adopt the fresh optionId so the saved preference stays current
            if (matchingTime.optionId !== savedPref.optionId) {
              saveSlotPreference({
                routeId: matchingTime.routeId,
                optionId: matchingTime.optionId,
                utcStartTime: matchingTime.utcStartTime,
                activityType: matchingTime.activityType,
                displayDate: savedPref.displayDate,
                displayTime: savedPref.displayTime,
                displayEndRange: savedPref.displayEndRange
              });
            }
          }
        }
      }

      sendLoadEvent(true, timeSlotsResult?.metadata);
      setIsInitialized(true);
    } catch (error: unknown) {
      telemetryService.trackError({
        name: 'transfer-select-slot:initialize:error',
        error: error
      });
      setHasError(true);
    }
  }, [
    orderSummaryDispatch,
    transferScheduleDispatch,
    sendLoadEvent,
    saveSlotPreference,
    getSavedSlotPreference
  ]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;

      const abortController = new AbortController();

      load(initialize(), abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [load, initialize]);

  const handleContinue = useCallback(() => {
    const { selectedTimeSlot } = transferScheduleState;

    if (!selectedTimeSlot.optionId || !selectedTimeSlot.routeId || !selectedTimeSlot.time) {
      return;
    }

    saveSlotPreference({
      routeId: selectedTimeSlot.routeId,
      optionId: selectedTimeSlot.optionId,
      utcStartTime: selectedTimeSlot.utcStartTime,
      activityType: selectedTimeSlot.activityType,
      displayDate: selectedTimeSlot.date ?? '',
      displayTime: selectedTimeSlot.time,
      displayEndRange: selectedTimeSlot.endRange
    });

    sendCompleteEvent(true);
    navigateToNextStep();
  }, [transferScheduleState, saveSlotPreference, sendCompleteEvent, navigateToNextStep]);

  const isTimeSlotSelected = Boolean(
    transferScheduleState.selectedTimeSlot.optionId &&
      transferScheduleState.selectedTimeSlot.routeId &&
      transferScheduleState.selectedTimeSlot.time
  );

  const hasSchedulingOptions =
    transferScheduleState.timeSlots.length > 0 || transferScheduleState.timeWindows.length > 0;

  const transferType = getTransferTypeNormalized(orderSummaryState.orderSummary.transfer);
  const transferTypeText = getTransferTypeFriendly(orderSummaryState.orderSummary.transfer);

  const hasCosigner =
    orderSummaryState.orderSummary.cobuyer &&
    orderSummaryState.orderSummary.cobuyer.emailAddress.trim() !== '' &&
    `${orderSummaryState.orderSummary.cobuyer.firstName} ${orderSummaryState.orderSummary.cobuyer.lastName}`.trim() !==
      '';

  return {
    isInitialized,
    hasError,
    hasSchedulingOptions,
    isTimeSlotSelected,
    handleContinue,
    transferType,
    transferTypeText,
    hasCosigner,
    vinEtchingState: transferScheduleState.vinEtchingState,
    vinEtchingLoading: transferScheduleState.vinEtchingLoading
  };
};

export default useTransferSelectSlotVm;
